import { alpha } from '@mui/system';
import { themeObject, validationsMessages } from 'lib';
import { javascriptTransformFormStyles } from 'pages/pipelines/CreateOrUpdateStep/components/javascript-transform-form-helper';
import * as yup from 'yup';
import { Control, FieldError } from 'react-hook-form';
import { pipelineFormStyles } from '../CreateOrUpdatePipeline/components/pipeline-form-helper';
import { EditorView, Extension } from '@uiw/react-codemirror';

export interface TrialRunModalProps {
    open: boolean;
    handleCloseButton: () => void;
    trialRunId: string;
    trialRunType: string;
    trialRunPageHeading: string;
    accordionConfig?: {
        heading: string;
        children: React.ReactNode;
    };
}

export interface TrialRunModalHeaderProps {
    isResultVisible: boolean;
    handleBackButton: () => void;
    handleCloseClick: () => void;
    trialRunPageHeading: string;
}

export interface TrialRunModalFooterProps {
    isResultVisible: boolean;
    handleRunClick: () => void;
}

export interface TrialRunJsonDataEditorProps {
    control?: Control<any>;
    value: string;
    errors?: FieldError;
    readOnlyEditor: boolean;
    onEditorValueChange?: (value: string) => void;
    codeMirrorHeight?: string;
}

export interface TrialRunFormData {
    data: string;
}

export const PipelineTrialRunSchema = yup.object({
    data: yup.string().required(validationsMessages.required),
});

export const PipelineTrialRunDefaults = {
    data: '',
};

export enum AccordionPanels {
    Panel0 = 'panel0',
    Panel1 = 'panel1',
}

export const trialRunModalStyles = {
    ...javascriptTransformFormStyles,
    ...pipelineFormStyles,
    root: {
        '& .MuiDialog-container': {
            backgroundColor: 'rgb(249,251,251)',
        },
        '& .MuiPaper-root': {
            boxShadow: 'none',
            maxWidth: '864px',
            maxHeight: 'none',
            height: '100%',
            overflow: 'auto',
        },
    },
    closeButtonStyles: {
        borderRadius: '48px',
        backgroundColor: themeObject.palette.grayScale[50],
        height: '22px',
        width: '22px',
    },
    headerContainer: {
        display: 'flex',
        alignItems: 'center',
        mx: '56px',
        mt: '40px',
    },
    bodyContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    headingText: {
        fontFamily: 'Raleway',
        fontSize: '26px',
        fontWeight: 500,
        lineHeight: '30.52px',
        textAlign: 'center',
        color: themeObject.palette.primary[700],
        mb: 2,
    },
    trialRunContainerStyles: {
        display: 'flex',
        flexDirection: 'column',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        mt: 2,
        mb: 0.5,
        mx: '98px',
    },
    codeMirrorContainer: {
        boxShadow: `0px 0px 3px 0px ${alpha(themeObject.palette.grayScale[700], 0.2)},0px 1px 1px 0px ${alpha(themeObject.palette.grayScale[700], 0.22)},0px 2px 1px -1px ${alpha(themeObject.palette.grayScale[700], 0.2)}`,
        borderRadius: '10px',
        mx: '98px',

        '& .icon-button': {
            height: '20px',
            width: '20px',
        },
    },
    codeMirrorFieldStyles: {
        mx: '50.59px',
        mb: '38px',
        mt: '8px',
    },
    trialRunResponseContainer: {
        p: 2,
        mt: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    trialRunResponseHeading: {
        fontFamily: 'Raleway',
        fontWeight: 600,
        fontSize: '18px',
        color: `${themeObject.palette.primary[700]} !important`,
        '& .MuiFormLabel-asterisk': {
            fontFamily: ['Red Hat Text', 'Open Sans', 'sans serif'].join(','),
        },
    },
    successResultsLabelContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    readOnlyContainerStyles: {
        width: '117px',
        position: 'absolute',
        top: '-16px',
        right: 0,
    },
    wrapperContainer: {
        width: '100%',
        height: '32px',
        alignContent: 'center',
        borderRadius: '0px 10px',
        backgroundColor: themeObject.palette.tertiary.purple[400],
    },
    accordionContainer: {
        overflow: 'hidden !important',
    },
    accordionDetailsListItemContainer: {
        p: 0,
        fontSize: '13px',
        overflowY: 'scroll',
    },
    listItemsContainer: {
        position: 'relative',
        maxHeight: '346px',
    },
    accordionDetailsEditorContainer: {
        p: 0,
        fontSize: '13px',
    },
};

export const ErrorResponseCodeMirrorStyles = (): Extension =>
    EditorView.theme({
        '.cm-content': {
            color: themeObject.palette.tertiary.red[100],
        },
    });
